function initProductShowcase(root) {
  if (root.dataset.showcaseInit) return;
  root.dataset.showcaseInit = '1';

  const viewport = root.querySelector('[data-showcase-viewport]');
  const track = viewport ? viewport.querySelector('.product-showcase__track') : null;
  if (!track) return;

  const captions = Array.from(root.querySelectorAll('.product-showcase__caption'));
  const prevButton = root.querySelector('.product-showcase__nav--prev');
  const nextButton = root.querySelector('.product-showcase__nav--next');

  const realCount = Number(track.dataset.realCount) || 0;
  if (realCount < 2) return;

  const rtl = getComputedStyle(track).direction === 'rtl';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const firstSet = Array.from(track.children).slice(0, realCount);

  let items = [];
  let wraps = [];
  let veils = [];
  let baseCenters = [];
  let spacing = 0;
  let period = 0;
  let x = 0;
  let velocity = 0;
  let settleRaf = null;
  let currentK = 0;
  let lastActive = -1;

  // Focus and the theme editor may native-scroll the clipped viewport;
  // position is transform-driven, so undo any real scroll.
  viewport.addEventListener('scroll', () => viewport.scrollTo(0, 0));

  function wrapAt(value) {
    let m = value % period;
    if (m < 0) m += period;
    return rtl ? m : m - period;
  }

  function vpCenter() {
    return viewport.clientWidth / 2;
  }

  function render() {
    const tx = wrapAt(x);
    track.style.transform = `translate3d(${tx}px, 0, 0)`;
    const center = vpCenter();
    let bestK = 0;
    let bestAbs = Infinity;
    for (let j = 0; j < items.length; j++) {
      const dist = baseCenters[j] + tx - center;
      const abs = Math.abs(dist);
      if (abs < bestAbs) {
        bestAbs = abs;
        bestK = j;
      }
      const d = abs / spacing;
      if (d < 4) {
        const scale = Math.max(0.82, 1.15 - 0.25 * Math.min(d, 2));
        wraps[j].style.transform = `scale(${scale})`;
        veils[j].style.opacity = Math.min(0.65, 0.5 * d).toFixed(3);
        items[j].style.zIndex = d < 0.5 ? 2 : 1;
      }
    }
    currentK = bestK;
    const realActive = Number(items[bestK].dataset.realIndex) || 0;
    if (realActive !== lastActive) {
      lastActive = realActive;
      captions.forEach((caption) => {
        caption.classList.toggle('is-active', Number(caption.dataset.realIndex) === realActive);
      });
    }
  }

  // How far x must shift so the item nearest the given position sits centered.
  function centerCorrection(value) {
    const tx = wrapAt(value);
    const center = vpCenter();
    let best = 0;
    let bestAbs = Infinity;
    for (let j = 0; j < items.length; j++) {
      const dist = baseCenters[j] + tx - center;
      if (Math.abs(dist) < bestAbs) {
        bestAbs = Math.abs(dist);
        best = dist;
      }
    }
    return -best;
  }

  function stopSettle() {
    if (settleRaf) cancelAnimationFrame(settleRaf);
    settleRaf = null;
  }

  function settleTo(delta, duration) {
    stopSettle();
    if (reducedMotion || duration <= 0 || Math.abs(delta) < 0.5) {
      x += delta;
      render();
      return;
    }
    const from = x;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      x = from + delta * eased;
      render();
      settleRaf = t < 1 ? requestAnimationFrame(step) : null;
    };
    settleRaf = requestAnimationFrame(step);
  }

  // Carry the fling momentum, then land with an item exactly centered.
  function settleToNearest(extra) {
    const delta = extra + centerCorrection(x + extra);
    const speed = Math.max(Math.abs(velocity), 0.4);
    const duration = Math.min(900, Math.max(300, (Math.abs(delta) / speed) * 1.8));
    settleTo(delta, duration);
  }

  function setup() {
    items = Array.from(track.children);
    if (items.length < 2) return;
    spacing = Math.abs(items[1].offsetLeft - items[0].offsetLeft);
    period = spacing * realCount;
    if (!spacing) return;
    // The strip must stay wider than the viewport by at least one full set
    // for the wrap to be invisible; clone extra sets on wide screens.
    let guard = 0;
    while (track.scrollWidth < viewport.clientWidth + period * 2 + spacing && guard < 10) {
      firstSet.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        track.appendChild(clone);
      });
      guard += 1;
    }
    items = Array.from(track.children);
    wraps = items.map((item) => item.querySelector('.product-showcase__image-wrap'));
    veils = items.map((item) => item.querySelector('.product-showcase__image-veil'));
    const tx = wrapAt(x);
    track.style.transform = `translate3d(${tx}px, 0, 0)`;
    const viewRect = viewport.getBoundingClientRect();
    baseCenters = items.map((item) => {
      const rect = item.getBoundingClientRect();
      return rect.left + rect.width / 2 - viewRect.left - tx;
    });
    // Keep the current product centered (start from the middle one).
    const active = lastActive === -1 ? Math.floor((realCount - 1) / 2) : lastActive;
    const home = realCount + active;
    x = vpCenter() - baseCenters[home];
    render();
  }

  setup();
  window.addEventListener('load', setup, { once: true });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setup, 150);
  });

  let pointerId = null;
  let startX = 0;
  let lastX = 0;
  let lastMoveTime = 0;
  let dragged = false;

  viewport.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary) return;
    pointerId = event.pointerId;
    startX = lastX = event.clientX;
    lastMoveTime = performance.now();
    dragged = false;
    velocity = 0;
    stopSettle();
  });

  viewport.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;
    const now = performance.now();
    const dx = event.clientX - lastX;
    lastX = event.clientX;
    if (!dragged && Math.abs(event.clientX - startX) > 6) {
      dragged = true;
      if (viewport.setPointerCapture) viewport.setPointerCapture(event.pointerId);
    }
    if (!dragged) return;
    const dt = Math.max(now - lastMoveTime, 1);
    lastMoveTime = now;
    x += dx;
    velocity = 0.8 * velocity + 0.2 * (dx / dt);
    render();
  });

  function endDrag(event) {
    if (event.pointerId !== pointerId) return;
    pointerId = null;
    if (!dragged) return;
    // A pause before lifting the finger means "stop here" — no fling.
    if (performance.now() - lastMoveTime > 80) velocity = 0;
    const capped = Math.max(-3, Math.min(3, velocity));
    settleToNearest(capped * 350);
    velocity = 0;
  }

  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);

  viewport.addEventListener(
    'click',
    (event) => {
      if (dragged) {
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }
    },
    true
  );

  viewport.addEventListener('dragstart', (event) => event.preventDefault());

  let wheelTimer;
  viewport.addEventListener(
    'wheel',
    (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      stopSettle();
      x -= event.deltaX;
      render();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => settleToNearest(0), 160);
    },
    { passive: false }
  );

  function stepBy(direction) {
    const target = currentK + direction;
    if (!items[target]) return;
    velocity = 0;
    settleTo(-(baseCenters[target] + wrapAt(x) - vpCenter()), 350);
  }

  if (prevButton) prevButton.addEventListener('click', () => stepBy(-1));
  if (nextButton) nextButton.addEventListener('click', () => stepBy(1));

  // Clicking a side product centers it; clicking the centered one navigates.
  track.addEventListener('click', (event) => {
    const link = event.target.closest('.product-showcase__link');
    if (!link) return;
    const item = link.closest('.product-showcase__item');
    const j = items.indexOf(item);
    if (j !== -1 && j !== currentK) {
      event.preventDefault();
      velocity = 0;
      settleTo(-(baseCenters[j] + wrapAt(x) - vpCenter()), 350);
    }
  });

  track.addEventListener('focusin', (event) => {
    const item = event.target.closest('.product-showcase__item');
    const j = items.indexOf(item);
    if (j === -1) return;
    stopSettle();
    x += -(baseCenters[j] + wrapAt(x) - vpCenter());
    render();
  });
}

document.querySelectorAll('[data-product-showcase]').forEach(initProductShowcase);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-product-showcase]').forEach(initProductShowcase);
});
